import React from "react"
import { COLORS, TYPOGRAPHY, SPACING, BORDER_RADIUS, SHADOWS } from "../constants/theme"

const PaginationNew = ({ page, totalPages, total, limit, onPageChange, onLimitChange }) => {
	// Generate page numbers to show
	const getPageNumbers = () => {
		const delta = 1 // Number of pages to show on each side of current page (max 3 pages total)
		const pages = []
		const start = Math.max(1, page - delta)
		const end = Math.min(totalPages, page + delta)

		// Add first page and ellipsis if needed
		if (start > 1) {
			pages.push(1)
			if (start > 2) {
				pages.push("...")
			}
		}

		// Add pages around current page
		for (let i = start; i <= end; i++) {
			pages.push(i)
		}

		// Add ellipsis and last page if needed
		if (end < totalPages) {
			if (end < totalPages - 1) {
				pages.push("...")
			}
			pages.push(totalPages)
		}

		return pages
	}

	const pageNumbers = getPageNumbers()

	// Styles
	const containerStyle = {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexWrap: "wrap",
		gap: SPACING.md,
		marginTop: SPACING.sm,
		padding: `${SPACING.lg} ${SPACING.xl}`,
		// backgroundColor: COLORS.white,
		// borderRadius: BORDER_RADIUS.lg,
		// boxShadow: SHADOWS.sm,
		// border: `1px solid ${COLORS.borderLight}`,
		fontFamily: TYPOGRAPHY.fontFamily,
	}

	const paginationControlsStyle = {
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
		flexWrap: "wrap",
	}

	const pageButtonStyle = (isActive = false, isDisabled = false, isEllipsis = false) => ({
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: isEllipsis ? "auto" : "31px",
		height: "31px",
		padding: isEllipsis ? `0 ${SPACING.xs}` : `0 ${SPACING.sm}`,
		borderRadius: BORDER_RADIUS.md,
		border: isActive ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.borderLight}`,
		backgroundColor: isActive ? COLORS.primary : COLORS.white,
		color: isActive ? COLORS.white : isDisabled ? COLORS.gray400 : COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontWeight: isActive ? TYPOGRAPHY.fontWeight.semibold : TYPOGRAPHY.fontWeight.normal,
		cursor: isDisabled || isEllipsis ? "default" : "pointer",
		transition: "all 0.2s ease",
		opacity: isDisabled ? 0.5 : 1,
		fontFamily: TYPOGRAPHY.fontFamily,
		outline: "none",
		boxShadow: isActive ? `0 2px 4px ${COLORS.primary}33` : "none",
	})

	const navButtonStyle = (isDisabled = false) => ({
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
		height: "31px",
		padding: `0 ${SPACING.md}`,
		borderRadius: BORDER_RADIUS.md,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.white,
		color: isDisabled ? COLORS.gray400 : COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontWeight: TYPOGRAPHY.fontWeight.medium,
		cursor: isDisabled ? "not-allowed" : "pointer",
		transition: "all 0.2s ease",
		opacity: isDisabled ? 0.5 : 1,
		fontFamily: TYPOGRAPHY.fontFamily,
		outline: "none",
		whiteSpace: "nowrap",
	})

	const infoStyle = {
		fontSize: TYPOGRAPHY.fontSize.sm,
		color: COLORS.gray600,
		fontFamily: TYPOGRAPHY.fontFamily,
		display: "flex",
		alignItems: "center",
		gap: SPACING.xs,
	}

	const selectStyle = {
		height: "31px",
		padding: `0 ${SPACING.md}`,
		borderRadius: BORDER_RADIUS.md,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.white,
		color: COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.sm,
		fontFamily: TYPOGRAPHY.fontFamily,
		cursor: "pointer",
		outline: "none",
		minWidth: "120px",
	}

	const separatorStyle = {
		height: "20px",
		width: "1px",
		backgroundColor: COLORS.borderLight,
		margin: `0 ${SPACING.sm}`,
	}

	const handlePageClick = (pageNumber) => {
		if (pageNumber !== "..." && pageNumber !== page) {
			onPageChange(pageNumber)
		}
	}

	// Handle hover effects
	const handleMouseEnter = (e, isDisabled, isActive) => {
		if (!isDisabled && !isActive) {
			e.target.style.backgroundColor = COLORS.gray50
			e.target.style.borderColor = COLORS.gray300
			e.target.style.transform = "translateY(-1px)"
		}
	}

	const handleMouseLeave = (e, isDisabled, isActive) => {
		if (!isDisabled && !isActive) {
			e.target.style.backgroundColor = COLORS.white
			e.target.style.borderColor = COLORS.borderLight
			e.target.style.transform = "translateY(0)"
		}
	}

	const startRecord = (page - 1) * limit + 1
	const endRecord = Math.min(page * limit, total)

	// Check if mobile
	const isMobile = typeof window !== "undefined" && window.innerWidth <= 768

	return (
		<div style={containerStyle}>
			{/* Left: Records info */}
			<div style={infoStyle}>
				<span>
					{isMobile ? (
						<>
							หน้า {page} / {totalPages} • {total.toLocaleString()} รายการ
						</>
					) : (
						<>
							แสดง <strong>{startRecord.toLocaleString()}</strong> - <strong>{endRecord.toLocaleString()}</strong> จากทั้งหมด{" "}
							<strong>{total.toLocaleString()}</strong> รายการ
						</>
					)}
				</span>
			</div>

			{/* Center: Pagination controls */}
			<div style={paginationControlsStyle}>
				{/* Mobile: Show only essential buttons */}
				{isMobile ? (
					<>
						<button
							onClick={() => handlePageClick(page - 1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าก่อนหน้า"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>

						<div
							style={{
								...infoStyle,
								padding: `0 ${SPACING.md}`,
								fontWeight: TYPOGRAPHY.fontWeight.semibold,
							}}
						>
							{page} / {totalPages}
						</div>

						<button
							onClick={() => handlePageClick(page + 1)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าถัดไป"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</>
				) : (
					<>
						{/* Desktop: Show full pagination */}
						<button
							onClick={() => handlePageClick(1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าแรก"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							หน้าแรก
						</button>

						<button
							onClick={() => handlePageClick(page - 1)}
							disabled={page === 1 || totalPages === 1}
							style={navButtonStyle(page === 1 || totalPages === 1)}
							title="หน้าก่อนหน้า"
							onMouseEnter={(e) => handleMouseEnter(e, page === 1 || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === 1 || totalPages === 1, false)}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							ก่อนหน้า
						</button>

						<div style={separatorStyle} />

						{/* Page numbers */}
						{pageNumbers.map((pageNum, index) => (
							<button
								key={index}
								onClick={() => handlePageClick(pageNum)}
								style={pageButtonStyle(pageNum === page, pageNum === "...")}
								disabled={pageNum === "..." || totalPages === 1}
								onMouseEnter={(e) => handleMouseEnter(e, pageNum === "..." || totalPages === 1, pageNum === page)}
								onMouseLeave={(e) => handleMouseLeave(e, pageNum === "..." || totalPages === 1, pageNum === page)}
							>
								{pageNum}
							</button>
						))}

						<div style={separatorStyle} />

						<button
							onClick={() => handlePageClick(page + 1)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าถัดไป"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							ถัดไป
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>

						<button
							onClick={() => handlePageClick(totalPages)}
							disabled={page === totalPages || totalPages === 1}
							style={navButtonStyle(page === totalPages || totalPages === 1)}
							title="หน้าสุดท้าย"
							onMouseEnter={(e) => handleMouseEnter(e, page === totalPages || totalPages === 1, false)}
							onMouseLeave={(e) => handleMouseLeave(e, page === totalPages || totalPages === 1, false)}
						>
							หน้าสุดท้าย
						</button>
					</>
				)}
			</div>

			{/* Right: Per-page selector */}
			<div style={{ display: "flex", alignItems: "center", gap: SPACING.md }}>
				<span
					style={{
						fontSize: TYPOGRAPHY.fontSize.sm,
						color: COLORS.gray600,
						display: isMobile ? "none" : "block",
					}}
				>
					{/* แสดงต่อหน้า: */}
				</span>
				<select value={limit} onChange={(e) => onLimitChange(Number(e.target.value))} style={selectStyle}>
					<option value={10}>10 รายการ</option>
					<option value={20}>20 รายการ</option>
					<option value={50}>50 รายการ</option>
					<option value={100}>100 รายการ</option>
				</select>
			</div>
		</div>
	)
}

export default PaginationNew
